/**
 * `gamification-core` — projection of melodimals' local `Progress` into
 * GameEvents. Pure and deterministic: the same `Progress` produces the same
 * `PlayerState` (computed on the client; there is no server or sync for the
 * child's progress—it lives in localStorage, see `storage/progress-store.ts`).
 *
 * UNIFIES the existing sticker system with the kit without duplicating it: each
 * earned sticker becomes gamification events (XP/counters/badges), and play days
 * feed the friendly streak. It neither adds nor mutates data: it is a
 * read of what the app already persists.
 *
 * DOCUMENTED LIMITATION: stickers are stored as a list WITHOUT timestamps,
 * so their temporal order is unknown; they receive a stable synthetic time
 * (irrelevant for XP/counters/badges, which are order-independent). The streak
 * IS real: it is derived from `playDays` (day keys the app records upon activity
 * completion).
 */
import { applyEvents, initialState } from "./engine";
import { MELODIMALS_EVENTS, melodimalsGamificationConfig } from "./config";
import { dayKeyUTC } from "./streaks";
import type { Progress } from "../storage/progress-store";
import type { GameEvent, PlayerState } from "./types";

/** Synthetic base for ordering stickers without timestamps (deterministic). */
const SYNTHETIC_BASE = Date.parse("2020-01-01T00:00:00.000Z");

/**
 * Translates local `Progress` into gamification events in ascending time
 * order (as the streak reducer expects). Each `game:*` sticker → game
 * completed; `song:*` → song learned; EVERY sticker → `sticker.earned`.
 * Each day in `playDays` → a play-day event (feeds the streak).
 */
export function deriveGameEvents(progress: Progress): GameEvent[] {
  const events: GameEvent[] = [];

  // Play days → friendly streak (real time derived from the day key).
  for (const day of progress.playDays) {
    const at = Date.parse(`${day}T12:00:00.000Z`);
    if (Number.isNaN(at)) {
      continue;
    }
    events.push({ type: MELODIMALS_EVENTS.playDay, at, day });
  }

  // Earned stickers → XP/counters/badges (stable synthetic time).
  progress.stickers.forEach((stickerId, i) => {
    const at = SYNTHETIC_BASE + i * 1000;
    const base = { at, day: dayKeyUTC(at), meta: { stickerId } };
    events.push({ type: MELODIMALS_EVENTS.stickerEarned, ...base });
    if (stickerId.startsWith("game:")) {
      events.push({ type: MELODIMALS_EVENTS.gameCompleted, ...base });
    } else if (stickerId.startsWith("song:")) {
      events.push({ type: MELODIMALS_EVENTS.songLearned, ...base });
    }
  });

  return events.toSorted((a, b) => a.at - b.at);
}

/**
 * Game state derived from `Progress`. Pure: same progress → same state.
 * Returns XP/level/streak/badges ready for the "My progress" UI.
 */
export function deriveGameState(progress: Progress): PlayerState {
  const events = deriveGameEvents(progress);
  return applyEvents(initialState(), events, melodimalsGamificationConfig).state;
}
